"use client";

import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/shared/ui/buttons/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/cards/card";
import { useMatchVetoState, useMatchVetoPick } from "@/entities/match-veto";
import { handleError } from "@/shared/api/error-handler";
import { cn } from "@/shared/lib/utils";

interface MatchSideVetoBlockProps {
  matchId: string;
}

export function MatchSideVetoBlock({ matchId }: MatchSideVetoBlockProps) {
  const tUi = useTranslations("Ui");
  const t = useTranslations("Match");
  const { vetoState, isLoading, refetch } = useMatchVetoState(matchId);
  const pickMutation = useMatchVetoPick(matchId);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!vetoState?.turnDeadlineUtc || vetoState.status !== "InProgress") {
      setSecondsLeft(null);
      return;
    }
    const deadline = new Date(vetoState.turnDeadlineUtc).getTime();
    const update = () => {
      const now = Date.now();
      if (now >= deadline) {
        setSecondsLeft(0);
        refetch();
        return;
      }
      setSecondsLeft(Math.max(0, Math.ceil((deadline - now) / 1000)));
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [vetoState?.turnDeadlineUtc, vetoState?.status, refetch]);

  if (isLoading || !vetoState) return null;

  const handlePick = (side: "Blue" | "Red") => {
    pickMutation.mutate(
      { side },
      {
        onSuccess: () => toast.success(t("vetoCompleted")),
        onError: (err) => handleError(err, { component: "MatchSideVetoBlock", action: "pickSide" }),
      }
    );
  };

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <CardTitle className="text-lg">{t("vetoTitle", { game: vetoState.gameIndex })}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {vetoState.status === "InProgress" && (
          <>
            <p className="text-sm text-muted-foreground">{t("vetoTurn")}</p>
            {secondsLeft !== null && (
              <p className="text-sm font-medium">
                {secondsLeft > 0 ? tUi("litMatchSideVetoBlock.nSek", { seconds: secondsLeft }) : t("vetoTimeout")}
              </p>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => handlePick("Blue")}
                disabled={pickMutation.isPending}
                className={cn("flex-1")}
              >
                {t("vetoPickBlue")}
              </Button>
              <Button
                variant="outline"
                onClick={() => handlePick("Red")}
                disabled={pickMutation.isPending}
                className={cn("flex-1")}
              >
                {t("vetoPickRed")}
              </Button>
            </div>
          </>
        )}
        {vetoState.status === "Completed" && (
          <p className="text-sm text-muted-foreground">{t("vetoCompleted")}</p>
        )}
        {vetoState.picks.length > 0 && (
          <div>
            <p className="text-sm font-medium mb-2">{t("vetoHistory")}</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              {vetoState.picks.map((p, i) => (
                <li key={i}>
                  Игра {p.gameIndex}: {p.side}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
